import { api } from '../api';

// ── Types ─────────────────────────────────────────────────────────────

export type OrgOverview = {
  id: string;
  name: string;
  slug: string;
  plan: string;
  billing_status: string;
  stats: {
    member_count: number;
    project_count: number;
    tokens_this_month: number;
  };
  settings: Record<string, unknown>;
  created_at: string;
};

export type OrgMember = {
  member_id: string;
  user_id: string;
  email: string;
  org_role: string;
  status: string;
  last_active_at: string | null;
  invited_by: string | null;
  joined_at: string;
  workspace_roles: Record<string, string>;
  user_created_at: string;
};

export type OrgInvite = {
  id: string;
  email: string;
  role: string;
  invite_code?: string;
  expires_at: string | null;
  created_at: string;
  invited_by: string | null;
  project_assignments: Array<{ project_id: string; role: string }>;
};

export type OrgUsage = {
  org_id: string;
  tokens_this_month: number;
  member_count: number;
  project_count: number;
  per_user: Array<{
    user_id: string;
    email: string;
    total_tokens: number;
  }>;
};

export type AuditEntry = {
  id: string;
  artifact_id: string;
  user_id: string;
  action: string;
  details: Record<string, unknown>;
  created_at: string;
};

// ── API calls ─────────────────────────────────────────────────────────

export const fetchOrg = async (): Promise<OrgOverview> =>
  api.get('api/admin/org').json();

export const updateOrg = async (data: {
  name?: string;
  settings?: Record<string, unknown>;
}): Promise<{ ok: boolean }> =>
  api.put('api/admin/org', { json: data }).json();

export const fetchMembers = async (): Promise<{ members: OrgMember[] }> =>
  api.get('api/admin/org/members').json();

export const updateMemberRole = async (
  userId: string,
  role: string,
): Promise<{ ok: boolean }> =>
  api.put(`api/admin/org/members/${userId}/role`, { json: { role } }).json();

export const removeMember = async (
  userId: string,
): Promise<{ ok: boolean }> =>
  api.delete(`api/admin/org/members/${userId}`).json();

export const fetchInvites = async (): Promise<{ invites: OrgInvite[] }> =>
  api.get('api/admin/org/invites').json();

export const createInvite = async (data: {
  email: string;
  name?: string;
  role?: string;
  project_assignments?: Array<{ project_id: string; role: string }>;
}): Promise<OrgInvite> =>
  api.post('api/admin/org/invites', { json: data }).json();

export const revokeInvite = async (
  inviteId: string,
): Promise<{ ok: boolean }> =>
  api.delete(`api/admin/org/invites/${inviteId}`).json();

export const fetchUsage = async (): Promise<OrgUsage> =>
  api.get('api/admin/org/usage').json();

export const fetchAuditLog = async (
  limit = 50,
  offset = 0,
): Promise<{ entries: AuditEntry[]; total: number }> =>
  api.get('api/admin/org/audit', { searchParams: { limit, offset } }).json();
